import React, { useEffect, useState } from 'react';
import * as locCon from '../LocalConstants';
import * as libCon from '../community-hats-js-library/Constants';
import CustomText from './CustomText';
import { UploadOutlined } from '@ant-design/icons';
import { generateRandomString, isNullOrUndefined } from '../community-hats-js-library/utils/generalFunctions';
import { Progress, Upload } from 'antd';
import { StateStoredValue } from '../hooks/StoreHooks';
import { uploadImageToBucket } from '../community-hats-js-library/utils/uploadFunctions';
import CustomButton from './CustomButton';
import CustomSpin from './CustomSpin';
import { CheckboxDefaultValue } from './CustomCheckBox';
import { showErrorDialogByCode } from '../utils/dialogFunctions';
import { RefContextLanguage } from '../providers/LanguageProvider';

export const SimpleImageUploadFromId = ({ valueID, ...props }) => {
  const [value, setValue] = StateStoredValue(valueID);
  const [imageUrl, setImageUrl] = useState(valueID + locCon.PREVIEW_VALUE);

  return (
    <SimpleImageUpload value={value} setValue={setValue} imageUrl={imageUrl} setImageUrl={setImageUrl} {...props} />
  );
};

export const SimpleImageUpload = ({
  value,
  setValue,
  title,
  infoText,
  infoImage = null,
  imageUrl,
  setImageUrl,
  includeCheckbox = false,
  defaultValue = libCon.NA,
  checkboxText,
}) => {
  const i18n = RefContextLanguage();

  const [uploadProgress, setUploadProgress] = useState(0);

  // The upload runs from the moment a file is picked, so the button has to
  // stand down for as long as the promise is in flight. Note this is not the
  // same as the bar reaching 100%: the bar tracks bytes handed to the socket,
  // and S3's answer, plus any retry, comes after that.
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);

  const [isImageValid, setIsImageValid] = useState(null);

  useEffect(() => {
    if (isNullOrUndefined(imageUrl)) {
      setIsImageValid(null);
      return;
    }

    const img = new window.Image();
    img.onload = () => setIsImageValid(true);
    img.onerror = () => setIsImageValid(false);
    img.src = imageUrl;
  }, [imageUrl]);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setImageUrl(file.url || file.preview);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const customUpload = async ({ file, onProgress, onSuccess, onError }) => {
    setUploadProgress(0);
    setUploadFailed(false);
    setIsUploading(true);

    const onProgressFun = (loaded, total) => {
      onProgress({ percent: Math.round((100 * loaded) / total) });
      setUploadProgress(Math.round((100 * loaded) / total));
    };

    try {
      // The same string becomes the S3 key and the value stored in Airtable, and
      // the key is signed for exactly as written, so anything the server would
      // refuse has to be replaced here rather than at the other end.
      const fileName = generateRandomString(5) + '_' + file.name.replaceAll(/[^A-Za-z0-9._-]/g, '_');

      const response = await uploadImageToBucket(libCon.SURVEY_IMAGES_FOLDER, fileName, file, onProgressFun);

      if (response === libCon.OK) {
        onSuccess();
        setValue(fileName);
      } else {
        setUploadFailed(true);
        showErrorDialogByCode(response);
        onError();
      }
    } finally {
      // In a finally so that an unexpected throw cannot leave the button
      // replaced by a spinner that never stops.
      setIsUploading(false);
    }
  };

  useEffect(() => {
    // A failure never changes the value, so clearing the red bar here only ever
    // happens on the way to a new state: a successful upload, or the N/A box.
    setUploadFailed(false);

    if (isNullOrUndefined(value) || value === defaultValue) {
      setUploadProgress(0);
      setImageUrl(null);
    } else {
      setUploadProgress(100);
    }
  }, [value, defaultValue, setImageUrl]);

  return (
    <div className="verticalSection">
      {!isNullOrUndefined(title) ? <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{title}</CustomText> : <div></div>}
      {!isNullOrUndefined(infoText) ? <CustomText type={locCon.INFO_TEXT}>{infoText}</CustomText> : <div></div>}
      {!isNullOrUndefined(infoImage) ? (
        <img src={infoImage} alt={'info'} style={{ maxWidth: '50vh', maxHeight: '50vh', backgroundColor: 'white' }} />
      ) : (
        <div></div>
      )}

      <div className="verticalSection" style={{ maxWidth: '80%', marginTop: '3vh' }}>
        <Progress
          percent={uploadProgress}
          status={uploadFailed ? 'exception' : undefined}
          strokeColor={uploadFailed ? undefined : 'var(--primary-color-3)'}
          size={{ height: '1vh' }}
        />
      </div>

      <div
        className="verticalSection"
        style={{ maxWidth: '15vh', maxHeight: '15vh', width: '15vh', height: '15vh', marginBottom: '2vh' }}
      >
        {isNullOrUndefined(value) ? (
          <div style={{ width: '100%', height: '100%', backgroundColor: 'var(--background-color-2)' }}></div>
        ) : value === defaultValue ? (
          <div className="verticalSection" style={{ width: '100%', height: '100%', marginTop: '40%' }}>
            <CustomText type={locCon.SUCCESS_TEXT}>{defaultValue}</CustomText>
          </div>
        ) : isImageValid === true ? (
          <img src={imageUrl} alt={value} style={{ maxWidth: '100%', maxHeight: '100%' }} />
        ) : (
          <div className="verticalSection" style={{ width: '100%', height: '100%', marginTop: '40%' }}>
            <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('imageUploadedCorrectly')}</CustomText>
          </div>
        )}
      </div>

      {/* The Upload itself stays mounted while the request is in flight, since
          unmounting it would leave antd resolving a file it no longer tracks.
          Disabling it is what stops a second upload from starting on top of the
          first: the spinner sits inside the same clickable wrapper. */}
      <Upload
        customRequest={customUpload}
        showUploadList={false}
        maxCount={1}
        disabled={isUploading}
        onChange={({ file }) => handlePreview(file)}
      >
        <div className="verticalSection">
          {isUploading ? (
            <CustomSpin type={locCon.BUTTON_SPINNER} />
          ) : (
            <CustomButton type={locCon.PRIMARY_BUTTON} icon={<UploadOutlined />} style={{ width: '20vh' }}>
              {i18n.t('upload')}
            </CustomButton>
          )}
        </div>
      </Upload>

      {includeCheckbox ? (
        <CheckboxDefaultValue value={value} setValue={setValue} defaultValue={defaultValue} text={checkboxText} />
      ) : (
        <div></div>
      )}
    </div>
  );
};
